/**
 * Copy for the Storage surface (SPEC §10).
 *
 * Sentence case throughout; `Eyebrow` and `Badge` apply their own casing, so
 * pass them sentence-case text. Anything interpolating a value is a
 * **function**, because a per-row control needs an accessible name a screen
 * reader can tell from its neighbours.
 *
 * **The voice here is "how much room is left", never "what this costs".** STO-4
 * is superseded in its own words — "estimated monthly cost was the wrong
 * headline" — and §17's preamble keeps commercial figures out of the system
 * entirely. Nothing on this screen names a price, and nothing should learn to.
 *
 * **This file got shorter when the surface became a console.** The page used to
 * explain itself in paragraphs: what the two table columns meant, what the
 * manifest was for, which of the two holdings lines counted toward the total.
 * Where layout or a column header can carry the fact, the sentence is gone.
 * Where SPEC requires the fact and nothing structural can carry it — STO-3's
 * overlap, STO-6's soft-stage caveat, RET-8's non-cancellable term — it is
 * compressed to one line rather than deleted. Each of those is marked below.
 */
export const STORAGE_COPY = {
  /**
   * No title, eyebrow, or summary here. Those are the surface *header*'s, and
   * they live in `surfaces.copy.ts` with the other three — a surface named in
   * two places is a surface renamed in one.
   */

  /** Decimal units — these are contract figures (PLN-2, PLN-4). */
  units: ['B', 'KB', 'MB', 'GB', 'TB'],
  dateLocale: 'en-US',

  plan: {
    label: 'Plan',
    docs: 'Docs',
    docsEvidence: 'Docs + Evidence',
    band: (band: string) => `Band ${band}`,
    /** Docs is flat and unbanded by design (PLN-2) — not an unset value. */
    unbanded: 'Flat, unbanded',
  },

  /**
   * The four numbers an admin opens this page for, and the bar that shows how
   * the first one decomposes.
   *
   * **`used` and `available` are the same measurement from both ends**, and both
   * are here on purpose: "19.7 GB of 1 TB" answers "how much have we spent" and
   * "980.3 GB available" answers "how much room is left". An admin deciding
   * whether to keep uploading is asking the second one, and making them subtract
   * a terabyte in their head was the old surface's version of that answer.
   */
  summary: {
    heading: 'Storage capacity',
    used: {
      label: 'Storage used',
      /** The allowance, under the value it is measured against. */
      of: (allowance: string) => `of ${allowance}`,
    },
    active: {
      label: 'Active files',
    },
    recycleBin: {
      label: 'Recycle bin',
    },
    available: {
      label: 'Available',
      /** The complement of `percentUsed`, so the tile row ends on good news. */
      free: (percent: number) => `${String(percent)}% free`,
    },
    files: (count: number) => `${String(count)} ${count === 1 ? 'file' : 'files'}`,
    /**
     * The segmented bar's accessible name. The segments themselves are
     * `aria-hidden` — a `progressbar` reports one `aria-valuenow`, and three
     * stacked fills cannot honestly be that one number. The tiles above carry
     * the split as text, so nothing is lost.
     */
    meterLabel: 'Storage used against allowance',
    /**
     * Both lists in this section are named, because a screen reader announcing
     * "list, 4 items" twice in a row tells a reader nothing about which one they
     * have landed in — and the tiles and the bar's key repeat the same two words
     * ("Recycle bin") by design, since they describe the same bytes.
     */
    tilesLabel: 'Storage at a glance',
    legendLabel: 'What each segment of the bar shows',
    /** The bar's own key, since the segments carry no labels of their own. */
    legend: {
      active: 'Active',
      recycleBin: 'Recycle bin',
      free: 'Free',
    },
  },

  /**
   * PLN-11: storage never refuses an upload. The wording has to carry that, or
   * an admin reads a warning as an outage.
   */
  storage: {
    warning: 'Past 80% of your storage allowance. Uploads are never refused for this.',
    exceeded: 'Over your storage allowance. Uploads still succeed; the overage is invoiced.',
  },

  /**
   * The one thing on this page that might need acting on today.
   *
   * **It renders only when something warrants it.** A standing "all clear" is
   * the equal-weight problem this redesign exists to fix — a banner that is
   * always there is a banner nobody reads on the day it changes.
   */
  attention: {
    heading: 'Needs attention',
    /** Transfer is the metric that actually approaches a limit in practice. */
    transfer: (window: string, percent: number, remaining: string) =>
      `${window} transfer is at ${String(percent)}% · ${remaining} remaining`,
    transferExceeded: (window: string) => `${window} transfer is over its allowance`,
    storage: (percent: number, remaining: string) =>
      `Storage is at ${String(percent)}% · ${remaining} remaining`,
    storageExceeded: 'Storage is over its allowance',
    /** PLN-6's Evidence upgrade signal, promoted from a footnote to the callout. */
    rejected: (count: number) =>
      `${String(count)} ${count === 1 ? 'upload was' : 'uploads were'} refused for exceeding the per-file size limit`,
  },

  transfer: {
    heading: 'Transfer allowance',
    caption: 'Transfer used against allowance, by window',
    columns: {
      period: 'Period',
      used: 'Used',
      allowance: 'Allowance',
      status: 'Status',
    },
    windows: {
      rolling7d: '7 days',
      rolling30d: '30 days',
      annual: 'This term',
    },
    /** STO-1: the annual window is term-to-date, not the calendar year. */
    since: (date: string) => `Since ${date}`,
    percentUsed: (percent: number) => `${String(percent)}%`,
    /**
     * The per-row marker, replacing the old per-window sentences. The table
     * states the numbers; this says which row to look at twice.
     */
    over: 'Over',
    near: 'Near limit',
    /**
     * **STO-6, compressed into one line instead of three badges.**
     *
     * The old surface put `WOULD THROTTLE` on both rolling rows and `INVOICED,
     * NEVER THROTTLED` on the annual one, then explained the staging in a footer
     * — three chips saying two different kinds of thing, which read as a
     * contradiction. This says both facts once: which windows are *capable* of
     * refusing (PLN-10's asymmetry), and whether that capability is switched on
     * (PLN-12's stage). Neither fact is dropped; STO-6 requires both.
     */
    policy:
      'The 7- and 30-day windows can throttle downloads. The term allowance is invoiced, never throttled.',
    soft: 'Throttling is not enforced yet — caps are measured and recorded only.',
    throttling: 'Downloads past a rolling cap are refused until the next daily count.',
    asOf: (when: string) => `Counted to ${when}; today’s reads are added live.`,
    /** ADR-0016: a total with no as-of is a measurement nobody took. */
    notCounted: 'Transfer has not been counted yet. Today’s reads are shown alone.',
  },

  breakdown: {
    /**
     * **"By space", not "by agency" and not "by counterpart".** A row is a space, so "by
     * agency" would claim a uniqueness the model does not have — an agency may
     * hold several spaces (SPEC §17, PLN-8's per-space cap). "By space" is what it
     * literally is and tells an admin nothing about what they are looking at.
     * _Amended._ **A room is no longer named after a counterpart** (CON-22), so
     * the header, the caption and the first column all say "space" — which is
     * what the row literally is, and which now carries a name a person chose
     * rather than an agency's.
     */
    heading: 'Storage & transfer by space',
    caption: 'Storage and transfer for each space',
    /**
     * **The headers carry what a note used to.** PLN-8 and PLN-9 attribute in
     * opposite directions — stored bytes belong to whoever uploaded them, read
     * bytes to whoever read them — and a space heavy in one and light in the
     * other is the normal shape rather than an anomaly. Naming the actor in each
     * header says that structurally, so the sentence that used to say it is
     * gone.
     *
     * **`recipientTransfer` keeps its key and loses its label.** The field is
     * `recipientTransferBytes` on the wire and in storage, and SPEC §11 and
     * PLN-8 both keep it there — the predicate never consulted the role, so
     * renaming it would rewrite stored data on two hot paths for a cosmetic
     * gain. The *heading* is a different question: "Downloaded by counterpart"
     * described a pair, and a room holds up to twenty-five agencies (A-10), so
     * what it now says is that somebody other than you read them.
     */
    columns: {
      space: 'Space',
      files: 'Files',
      stored: 'Stored by you',
      share: '% of total',
      transfer: 'Downloaded by you',
      recipientTransfer: 'Downloaded by others',
    },
    /**
     * The one line the headers cannot carry. `shareOfTotal`'s denominator is
     * every held byte — active **and** binned — while the `Stored by you` column
     * beside it counts active bytes only. Without this the two columns look like
     * they should divide into each other, and they do not.
     */
    shareNote: 'Share is of all held bytes, including the recycle bin.',
    /**
     * **The keys and the words both name the membership status now.** These
     * badges render off `row.status`, whose values became `Invited`/`Active`/
     * `Removed` when a membership stopped describing one side of a pair
     * (ADR-0070) — and they went on reading "Severed" and "Pending", which are
     * the words for a link that was cut and a handshake nobody had answered.
     * An admin reading a storage table has no way to tell that "Severed" is
     * this row's `Removed`.
     */
    removed: 'Removed',
    invited: 'Invited',
    /** Bytes outlive access (CON-14, UPL-12), which is not obvious. */
    removedNote: 'Spaces you have left still hold your files, and still use your allowance.',
    empty: 'No spaces yet.',
  },

  /**
   * STO-3's second line, promoted out of a footnote into its own section — its
   * own words call it "the more important one".
   *
   * The bin became a tile in the summary above, because it is **reclaimable**.
   * This is the storage that is not, and that difference is the whole reason the
   * two no longer sit in one list called "Also held".
   */
  retention: {
    heading: 'Retained storage',
    /** Leads with the consequence, not the measurement. */
    locked: (bytes: string, files: string) =>
      `${bytes} across ${files} cannot be permanently deleted`,
    /**
     * The class labels carry their term, mirroring `folderView.copy.ts` — and
     * the term is the whole reason this breaks down at all. One year of Extend
     * and seven of Statutory are the same bytes and very different commitments
     * (RET-6, RET-8). `None` has no row here: a file on the ordinary window is
     * not a holding this reports.
     */
    classes: {
      Extend: 'Extend · 1 year',
      Preserve: 'Preserve · 3 years',
      Statutory: 'Statutory · 7 years',
    },
    row: (files: string, bytes: string) => `${files} · ${bytes}`,
    /**
     * **Two SPEC facts in one line**, down from two sentences.
     *
     * *Already counted* is STO-3's amendment: unlike the bin, these bytes are
     * inside the figures above, and a reader who sums the two sections gets a
     * number that means nothing. *Non-cancellable* is RET-8, and is what makes
     * this the more important of the two lines. Neither half is droppable.
     */
    note: 'Already counted above · non-cancellable until each term ends',
    /** An Evidence agency retaining nothing gets a measured zero (RET-5). */
    none: 'No files are under retention.',
  },

  rejected: {
    /** The detail behind the callout's one-liner. */
    hint: 'Docs limits a single file to 250 MB. Evidence has no per-file limit.',
    truncated: 'Only the most recent refusals were counted.',
  },

  series: {
    heading: 'Activity',
    /** STO-7: the evidence an agency argues its band from (PLN-5). */
    note: 'The record behind an annual band review.',
    caption: 'Daily transfer and storage over the current term',
    /**
     * The legend names what each colour on the chart *is* (bar segment or
     * line), and the peak figures beside them say how far it went. Naming only
     * the peaks, as this used to, left "what does the amber mean" unanswered.
     */
    transfer: 'Your downloads',
    recipientTransfer: 'Others’ downloads',
    storage: 'Stored',
    /**
     * Two charts, not one shared canvas — a bar in gigabytes-per-day and a
     * line in gigabytes-held are different kinds of measurement, and a reader
     * asked to read both off one set of axes has to first work out which
     * shape belongs to which scale. Splitting them means each caption can
     * name its own unit rather than a sentence trying to cover both.
     */
    charts: {
      transfer: {
        heading: 'Daily transfer',
        caption: (from: string, to: string) => `Daily transfer in gigabytes, ${from} to ${to}`,
      },
      storage: {
        heading: 'Stored',
        caption: (from: string, to: string) => `Total stored in gigabytes, ${from} to ${to}`,
      },
    },
    /**
     * The gridline label at the top of each chart's own plot area — **the
     * highest single day drawn**, not PLN-5's rolling-30-day evidence figure
     * below. The two are different numbers (a day's total versus a 30-day
     * sum) and labelling this gridline with the other one would be reporting
     * a number the chart does not draw.
     */
    axis: {
      peakDay: (value: string) => `Peak day: ${value}`,
      peak: (value: string) => `Peak: ${value}`,
      zero: '0',
    },
    /** A leading gap — the common real case, before the rollup existed. */
    gapNote: (through: string) => `Not measured through ${through}.`,
    /** PLN-5's evidence figures — the rolling-30-day peak and the term peak. */
    peaks: {
      transfer: (value: string) => `Peak 30-day: ${value}`,
      storage: (value: string) => `Peak: ${value}`,
      /** Absent means never sampled, which is not the same claim as zero. */
      storageUnknown: 'Peak: not yet measured',
    },
    range: (from: string, to: string) => `${from} to ${to}`,
    empty: 'No usage recorded for this term yet.',
    unavailable: 'The daily usage series could not be loaded.',
  },

  /**
   * EXIT-5's manifest.
   *
   * **The explanatory paragraph is gone and the button moved to the top.** The
   * note said what the document was *for* — a transferred storage account is
   * GUID-named blobs in GUID-named containers (SYS-3) — which was worth a
   * paragraph when the control sat at the bottom of a page of figures and had to
   * argue for itself. As a header action it reads as what an admin came to do,
   * and the label carries it.
   */
  exportManifest: {
    action: 'Export manifest',
  },

  states: {
    loading: 'Loading storage…',
    error: 'Storage could not be loaded. Reload and try again.',
    retry: 'Try again',
  },
} as const;
